import { useState } from "react";
import type { ChannelCatalogItem } from "../../../api/connectionHub.types";
import { isConnectionHubError } from "../../../utils/handleConnectionError";
import ConnectionErrorPanel from "../../common/ConnectionErrorPanel/ConnectionErrorPanel";
import ConnectionLogo from "../../common/ConnectionLogo/ConnectionLogo";
import ConnectModalShell from "../../modal/ConnectModalShell/ConnectModalShell";
import warningAlert from "../../../assets/disconnect-warning-alert.svg";
import warningMark from "../../../assets/disconnect-warning-mark.svg";
import styles from "./RemoveAccountDialog.module.scss";

export interface RemoveAccountDialogProps {
  visible: boolean;
  channel: ChannelCatalogItem;
  /** What the confirmation names, e.g. the login email or the store. */
  accountName: string;
  /**
   * The caller owns the call. Smart accounts disconnect by connectionId, quick ones by access token
   * id — two different identity spaces that should not meet inside this dialog.
   */
  onDisconnect: () => Promise<void>;
  onRemoved: () => void;
  onBack: () => void;
  onClose: () => void;
}

const RemoveAccountDialog = ({
  visible,
  channel,
  accountName,
  onDisconnect,
  onRemoved,
  onBack,
  onClose,
}: RemoveAccountDialogProps): JSX.Element => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const close = (): void => {
    if (!submitting) onClose();
  };
  const back = (): void => {
    if (!submitting) onBack();
  };

  const disconnect = async (): Promise<void> => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onDisconnect();
      onRemoved();
    } catch (caught) {
      // A quick disconnect rejects with a raw axios error, not a hub error.
      setError(isConnectionHubError(caught) ? caught.message : "Could not disconnect this account.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConnectModalShell
      visible={visible}
      compact
      busy={submitting}
      title={
        <span className={styles.channel}>
          <ConnectionLogo logoUrl={channel.logo} className={styles.logo} />
          {channel.ipName}
        </span>
      }
      onBack={back}
      primaryLabel="Disconnect"
      onPrimary={() => { void disconnect(); }}
      primaryLoading={submitting}
      primaryDisabled={submitting}
      bodyClassName={styles.content}
      headerClassName={styles.header}
      footerClassName={styles.footer}
      onClose={close}
      onDismiss={(reason) => { if (reason === "escape") close(); }}
    >
      <div className={styles.contentInner}>
        <span className={styles.mark} aria-hidden="true">
          <img src={warningMark} alt="" />
          <span>!</span>
        </span>
        <h3 className={styles.title}>Would you like to disconnect this {channel.ipName} account?</h3>
        <div className={styles.warning}>
          <img src={warningAlert} alt="" aria-hidden="true" />
          <span>You will no longer be able to see data from {accountName} for {channel.ipName} on Merito.</span>
        </div>
        <ConnectionErrorPanel error={error} className={styles.errorPanel} />
      </div>
    </ConnectModalShell>
  );
};

export default RemoveAccountDialog;
